import React, { forwardRef, useMemo } from 'react';

import { cn } from '@/shared/lib/tailwind-merge';

export enum TextSize {
    XS = 'xs',
    SM = 'sm',
    MD = 'md',
    LG = 'lg',
    XL = 'xl',
    XXL = 'xxl',
}

export enum TextType {
    Paragraph = 'p',
    Span = 'span',
    Label = 'label',
    Link = 'a',
    Display = 'display',
    Heading = 'h1',
}

export enum TextWeight {
    Light = 'light',
    Regular = 'regular',
    Medium = 'medium',
    Bold = 'bold',
    SemiBold = 'semiBold',
}

type TextElementMap = {
    [TextType.Paragraph]: React.HTMLAttributes<HTMLParagraphElement>;
    [TextType.Span]: React.HTMLAttributes<HTMLSpanElement>;
    [TextType.Label]: React.HTMLAttributes<HTMLLabelElement>;
    [TextType.Link]: React.AnchorHTMLAttributes<HTMLAnchorElement>;
    [TextType.Display]: React.HTMLAttributes<HTMLDivElement>;
    [TextType.Heading]: React.HTMLAttributes<HTMLHeadingElement>;
};

type TextProps<T extends TextType> = {
    size?: TextSize;
    name?: string;
    type?: T;
    fontWeight?: TextWeight;
    children: React.ReactNode;
    className?: string;
    class?: string;
} & (T extends keyof TextElementMap ? TextElementMap[T] : React.HTMLAttributes<HTMLElement>);

interface TextComponent extends React.FC<TextProps<TextType>> {
    sizes: typeof TextSize;
    weights: typeof TextWeight;
    types: typeof TextType;
}

const Text = forwardRef<TextComponent, TextProps<TextType>>(
    (
        {
            type = TextType.Paragraph,
            size = TextSize.MD,
            name,
            fontWeight = TextWeight.Regular,
            className,
            class: classname,
            children,
            ...props
        },
        ref,
    ) => {
        const Component = type as keyof JSX.IntrinsicElements as React.ElementType;

        const weightStyles = {
            [TextWeight.Light]: 'font-light',
            [TextWeight.Regular]: 'font-normal',
            [TextWeight.Medium]: 'font-medium',
            [TextWeight.Bold]: 'font-bold',
            [TextWeight.SemiBold]: 'font-semibold',
        };

        const textStyles: Partial<Record<TextType, Partial<Record<TextSize, string>>>> = {
            [TextType.Display]: {
                [TextSize.LG]: 'text-[48px] font-[650] leading-[56px] tracking-[-1.44px]',
                [TextSize.MD]: 'text-[36px] leading-[40px] tracking-[-1.08px]',
                [TextSize.SM]: 'text-[32px] leading-[40px] tracking-[-0.96px]',
                [TextSize.XS]: 'text-[28px] leading-[36px] tracking-[-0.84px]',
            },
            [TextType.Heading]: {
                [TextSize.XXL]: 'text-[32px] leading-[36px] tracking-[-0.96px]',
                [TextSize.XL]: 'text-[28px] leading-[32px] tracking-[-0.84px]',
                [TextSize.LG]: 'text-[24px] leading-[28px] tracking-[-0.72px]',
                [TextSize.MD]: 'text-[20px] leading-[24px] tracking-[-0.6px]',
                [TextSize.SM]: 'text-[16px] leading-[20px] tracking-[-0.48px]',
                [TextSize.XS]: 'text-[14px] leading-[20px] tracking-[-0.42px]',
            },
            [TextType.Label]: {
                [TextSize.LG]: 'text-[1rem] leading-[1.5rem] -tracking-[3%]',
                [TextSize.MD]: 'text-[0.875rem] leading-[1.25rem] -tracking-[3%]',
                [TextSize.SM]: 'text-[0.75rem] leading-[1rem] -tracking-[3%]',
                [TextSize.XS]: 'text-[0.625rem] leading-[0.875rem] -tracking-[3%]',
            },
            [TextType.Link]: {
                [TextSize.LG]: 'text-[1rem] leading-[1.5rem] -tracking-[3%]',
                [TextSize.MD]: 'text-[0.875rem] leading-[1.25rem] -tracking-[3%]',
                [TextSize.SM]: 'text-[0.75rem] leading-[1rem] -tracking-[3%]',
                [TextSize.XS]: 'text-[0.625rem] leading-[0.875rem] -tracking-[3%]',
            },
            [TextType.Paragraph]: {
                [TextSize.XL]: 'text-[20px] font-normal leading-[28px] tracking-[-0.6px]',
                [TextSize.LG]: 'text-[16px] leading-[20px] tracking-[-0.48px]',
                [TextSize.MD]: 'text-[14px] leading-[20px] tracking-[-0.42px]',
                [TextSize.SM]: 'text-[12px] leading-[16px] tracking-[-0.36px]',
                [TextSize.XS]: 'text-[10px] leading-[14px] tracking-[-0.3px]',
            },
            [TextType.Span]: {
                [TextSize.LG]: 'text-[0.625rem] leading-[0.875rem] tracking-[4%]',
                [TextSize.MD]: 'text-[0.625rem] leading-[0.875rem] tracking-[4%]',
                [TextSize.SM]: 'text-[0.625rem] leading-[0.875rem] tracking-[4%]',
                [TextSize.XS]: 'text-[0.625rem] leading-[0.875rem] tracking-[4%]',
            },
        };

        const computedClassNames = cn(
            'font-[Inter]',
            weightStyles[fontWeight],
            textStyles[type]?.[size],
            className,
            classname,
        );

        const testId = useMemo(() => {
            let testId = '';

            if (name) {
                testId = name.toLowerCase().replace(/\s/g, '-');

                return testId + '-text';
            }

            if (!name && typeof children === 'string') {
                testId = children.toLowerCase().replace(/\s/g, '-');

                return testId + '-text';
            }

            return `${size}-${type}-text`;
        }, [name, children]);

        return (
            <Component
                ref={ref}
                data-testid={testId}
                className={computedClassNames}
                {...(props as any)}
            >
                {children}
            </Component>
        );
    },
);

export const TextComponent = Object.assign(Text, {
    sizes: TextSize,
    types: TextType,
    weights: TextWeight,
});

export { TextComponent as Text };
